<script setup lang="ts">
import type { DataItem as DispatchJobsTableDataItem } from '@/components/tables/DispatchJobsTable.vue';
import type { DefDialogState, DialogName } from '@/stores/useDialogStore';

import { computed } from 'vue';

import SecondarySideBarMenuVListItem from '@/components/items/SecondarySideBarMenuVListItem.vue';
import { useCloneJobDialog } from '@/composables/useCloneJobDialog';
import { useJobEditDialog } from '@/composables/useJobEditDialog';
import { useDialogStore } from '@/stores/useDialogStore';
import { useDispatchDashboardViewStore } from '@/stores/useDispatchDashboardViewStore';
import * as dispatchHelpers from '@/helpers/dispatch';
import {
  fetchPostDespatchJobUndoAll,
  fetchPostJobAllocationBulk,
  fetchPostJobCancel,
  fetchPostJobEditLock,
  fetchPostJobEditUnlock,
  fetchPostJobFutile,
} from '@/helpers/fetchers';
import * as hotkeys from '@/constants/hotkeys';
import * as routeNames from '@/constants/routeNames';

export interface Props {
  dataItem?: DispatchJobsTableDataItem;
}
const props = withDefaults(defineProps<Props>(), {
  dataItem: undefined,
});
const emit = defineEmits<{
  done: [id: number];
}>();

const dispatchDashboardViewStore = useDispatchDashboardViewStore();
const jobEditDialog = useJobEditDialog();
const cloneJobDialog = useCloneJobDialog();
const dialogStore = useDialogStore();

const selectedDispatchJobs = computed(() => {
  const _dispatchJobs: DispatchJobsTableDataItem[] = [];
  if (!dispatchDashboardViewStore.data.dispatchJobs) return _dispatchJobs;
  const dataItemId = props.dataItem?.id;
  if (dataItemId) {
    const clickedJob = dispatchDashboardViewStore.data.dispatchJobs.find(
      (dispatchJob) => {
        return dispatchJob.id === dataItemId;
      },
    );
    if (clickedJob) {
      _dispatchJobs.push(props.dataItem);
    }
    return _dispatchJobs;
  } else {
    return dispatchDashboardViewStore.selectedJobs || _dispatchJobs;
  }
});

const firstSelectedId = computed(() => {
  return selectedDispatchJobs.value[0]?.originalJob.id || NaN;
});

const _jobIdAndJobDateQuery = computed(() => {
  if (!selectedDispatchJobs.value.length) return;
  return {
    job_id: selectedDispatchJobs.value[0].originalJob.rockhopper_id,
    job_date: selectedDispatchJobs.value[0].originalJob.rockhopper_date,
  };
});

const isReadyToAllocate = computed(() => {
  return selectedDispatchJobs.value.some((dispatchJob) => {
    return dispatchHelpers.checkReadyToAllocate(dispatchJob);
  });
});

const isReadyToPreAllocate = computed(() => {
  return selectedDispatchJobs.value.some((dispatchJob) => {
    return dispatchHelpers.checkReadyToPreAllocate(dispatchJob);
  });
});

const isReadyToDeallocate = computed(() => {
  return selectedDispatchJobs.value.some((dispatchJob) => {
    return dispatchHelpers.checkReadyToDeallocate(dispatchJob);
  });
});

const isReadyToAllocateAndSplit = computed(() => {
  return selectedDispatchJobs.value.some((dispatchJob) => {
    return dispatchHelpers.checkReadyToAllocateAndSplit(dispatchJob);
  });
});

const isReadyToLock = computed(() => {
  return selectedDispatchJobs.value.some((dispatchJob) => {
    return dispatchHelpers.checkReadyToLock(dispatchJob);
  });
});

const isReadyToUnlock = computed(() => {
  return selectedDispatchJobs.value.some((dispatchJob) => {
    return dispatchHelpers.checkReadyToUnlock(dispatchJob);
  });
});

// Lock/unlock run across every selected job; the label follows the first
// selected job — a locked target shows "Unlock Job" in place of the (otherwise
// disabled) "Lock Job".
const isTargetLocked = computed(() => {
  return !!selectedDispatchJobs.value[0]?.isLocked;
});

// On the Allocated tab every job is already allocated, so the allocate action
// flips to "Unalloc" (deallocate) and the standalone Deallocate item is hidden.
const isAllocatedTab = computed(() => {
  return dispatchDashboardViewStore.query.tab === 'allocated';
});

const isReadyToPickup = computed(() => {
  return selectedDispatchJobs.value.some((dispatchJob) => {
    return dispatchHelpers.checkReadyToPickup(dispatchJob);
  });
});

const isReadyToSplitToBase = computed(() => {
  return (
    selectedDispatchJobs.value.length === 1 &&
    selectedDispatchJobs.value.some((dispatchJob) => {
      return dispatchHelpers.checkReadyToSplitToBase(dispatchJob);
    })
  );
});

const isReadyToPoD = computed(() => {
  return selectedDispatchJobs.value.some((dispatchJob) => {
    return dispatchHelpers.checkReadyToPoD(dispatchJob);
  });
});

const isReadyToEditTimestamps = computed(() => {
  return (
    selectedDispatchJobs.value.length === 1 &&
    selectedDispatchJobs.value.some((dispatchJob) => {
      return dispatchHelpers.checkReadyToEditTimestamps(dispatchJob);
    })
  );
});

const isReadyToClone = computed(() => {
  return (
    selectedDispatchJobs.value.length === 1 &&
    selectedDispatchJobs.value.some((dispatchJob) => {
      return dispatchHelpers.checkReadyToClone(dispatchJob);
    })
  );
});

const isReadyToCancel = computed(() => {
  return selectedDispatchJobs.value.some((dispatchJob) => {
    return dispatchHelpers.checkReadyToCancel(dispatchJob);
  });
});

const isReadyToFutile = computed(() => {
  return selectedDispatchJobs.value.some((dispatchJob) => {
    return dispatchHelpers.checkReadyToFutile(dispatchJob);
  });
});

const isReadyToQuery = computed(() => {
  return (
    selectedDispatchJobs.value.length === 1 &&
    selectedDispatchJobs.value.some((dispatchJob) => {
      return dispatchHelpers.checkReadyToQuery(dispatchJob);
    })
  );
});

const jobs = computed(() => {
  const jobs: { jobId: number; jobDate: string }[] = [];
  selectedDispatchJobs.value.forEach((dispatchJob) => {
    jobs.push({
      jobId: dispatchJob.originalJob.rockhopper_id,
      jobDate: dispatchJob.originalJob.rockhopper_date,
    });
  });
  return jobs;
});

function handleAllocateClick() {
  if (!isReadyToAllocate.value) return;
  if (!jobs.value.length) return;

  const dialogName: DialogName = 'dispatchAssignDriver';
  dialogStore
    .open(dialogName, { jobs: jobs.value, action: 'allocate' })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        if (closeDetail === 'confirm') {
          emit('done', firstSelectedId.value);
        }
      },
    );
}

function handlePreAllocateClick() {
  if (!isReadyToPreAllocate.value) return;
  if (!jobs.value.length) return;

  const dialogName: DialogName = 'dispatchAssignDriver';
  dialogStore
    .open(dialogName, { jobs: jobs.value, action: 'preallocate' })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        if (closeDetail === 'confirm') {
          emit('done', firstSelectedId.value);
        }
      },
    );
}

function handleDeallocateClick() {
  if (!isReadyToDeallocate.value) return;
  if (!jobs.value.length) return;

  const dialogName: DialogName = 'dispatchConfirm';
  dialogStore
    .open(dialogName, {
      confirm: async () => {
        // Bulk: deallocate each selected job (the action follows its
        // allocation state).
        await fetchPostJobAllocationBulk(
          selectedDispatchJobs.value.map((dispatchJob) =>
            dispatchHelpers.getDeallocateActionItem(dispatchJob.originalJob),
          ),
        );
      },
      type: 'deallocate',
      jobs: jobs.value,
    })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        if (closeDetail === 'confirm') {
          emit('done', firstSelectedId.value);
        }
      },
    );
}

function handleLockClick() {
  if (!isReadyToLock.value) return;
  if (!jobs.value.length) return;

  const dialogName: DialogName = 'dispatchConfirm';
  dialogStore
    .open(dialogName, {
      confirm: async () => {
        // Bulk: attempt every selected job; per-job errors surface their own
        // notification while the rest still run.
        await Promise.allSettled(
          jobs.value.map((job) =>
            fetchPostJobEditLock({
              job_id: job.jobId,
              job_date: job.jobDate,
            }),
          ),
        );
      },
      type: 'lock',
      jobs: jobs.value,
    })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        if (closeDetail === 'confirm') {
          emit('done', firstSelectedId.value);
        }
      },
    );
}

function handleUnlockClick() {
  if (!isReadyToUnlock.value) return;
  if (!jobs.value.length) return;

  const dialogName: DialogName = 'dispatchConfirm';
  dialogStore
    .open(dialogName, {
      confirm: async () => {
        await Promise.allSettled(
          jobs.value.map((job) =>
            fetchPostJobEditUnlock({
              job_id: job.jobId,
              job_date: job.jobDate,
            }),
          ),
        );
      },
      type: 'unlock',
      jobs: jobs.value,
    })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        if (closeDetail === 'confirm') {
          emit('done', firstSelectedId.value);
        }
      },
    );
}

function handleSplitToBaseClick() {
  if (!isReadyToSplitToBase.value) return;
  if (!jobs.value.length) return;

  const dialogName: DialogName = 'splitToBase';
  dialogStore
    .open(dialogName, {
      job: {
        rockhopper_id: selectedDispatchJobs.value[0].originalJob.rockhopper_id,
        rockhopper_date:
          selectedDispatchJobs.value[0].originalJob.rockhopper_date,
        current_driver_code:
          selectedDispatchJobs.value[0].originalJob.stops.at(-1)?.driver_is ||
          NaN,
      },
    })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        if (closeDetail === 'confirm') {
          emit('done', firstSelectedId.value);
        }
      },
    );
}

function handleEditTimestampsClick() {
  if (!isReadyToEditTimestamps.value) return;
  if (!jobs.value.length) return;

  const dialogName: DialogName = 'editTimestamps';
  dialogStore
    .open(dialogName, {
      job: selectedDispatchJobs.value[0].originalJob,
    })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        if (closeDetail === 'confirm') {
          emit('done', firstSelectedId.value);
        }
      },
    );
}

function handleUndoAllClick() {
  if (!jobs.value.length) return;
  const job = jobs.value[0];

  const dialogName: DialogName = 'dispatchConfirm';
  dialogStore
    .open(dialogName, {
      confirm: async () => {
        return fetchPostDespatchJobUndoAll(
          { job_id: job.jobId, job_date: job.jobDate },
          undefined as never,
        );
      },
      type: 'undo',
      jobs: jobs.value,
    })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        if (closeDetail === 'confirm') {
          emit('done', firstSelectedId.value);
        }
      },
    );
}

function handleCloneClick() {
  if (!isReadyToClone.value) return;
  if (!jobs.value.length) return;

  const job = jobs.value[0];
  cloneJobDialog.open(job.jobId, job.jobDate, () => {
    emit('done', firstSelectedId.value);
  });
}

function handleCancelClick() {
  if (!isReadyToCancel.value) return;
  if (!jobs.value.length) return;
  const job = jobs.value[0];

  const dialogName: DialogName = 'dispatchConfirm';
  dialogStore
    .open(dialogName, {
      confirm: async () => {
        return fetchPostJobCancel({
          job_id: job.jobId,
          job_date: job.jobDate,
          actor: 'no actor', // TODO there is CancelCloseStopDialog with this input
          reason: 'no reason', // TODO there is CancelCloseStopDialog with this input
        });
      },
      type: 'cancel',
      jobs: jobs.value,
    })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        if (closeDetail === 'confirm') {
          emit('done', firstSelectedId.value);
        }
      },
    );
}

function handleFutileClick() {
  if (!isReadyToFutile.value) return;
  if (!jobs.value.length) return;
  const job = jobs.value[0];

  const dialogName: DialogName = 'dispatchConfirm';
  dialogStore
    .open(dialogName, {
      confirm: async () => {
        return fetchPostJobFutile({
          job_id: job.jobId,
          job_date: job.jobDate,
          reason: 'no reason', // TODO
          driver:
            selectedDispatchJobs.value[0].originalJob.stops.at(-1)?.driver_is ||
            NaN,
        });
      },
      type: 'futile',
      jobs: jobs.value,
    })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        if (closeDetail === 'confirm') {
          emit('done', firstSelectedId.value);
        }
      },
    );
}

function handleEditJobClick() {
  if (!selectedDispatchJobs.value.length) return;

  const allJobs = dispatchDashboardViewStore.dispatchJobsTableData.map(
    (item) => ({ jobId: item.jobId, jobDate: item.jobDate }),
  );

  jobEditDialog.open(
    selectedDispatchJobs.value[0].originalJob.rockhopper_id,
    selectedDispatchJobs.value[0].originalJob.rockhopper_date,
    allJobs,
    dispatchDashboardViewStore.data.refreshDispatchJobs,
  );
  emit('done', firstSelectedId.value);
}

function handleQueryClick() {
  if (!isReadyToQuery.value) return;
  if (!jobs.value.length) return;

  const dialogName: DialogName = 'newQuery';
  dialogStore
    .open(dialogName, {
      job: {
        rockhopper_id: selectedDispatchJobs.value[0].originalJob.rockhopper_id,
        rockhopper_date:
          selectedDispatchJobs.value[0].originalJob.rockhopper_date,
        rockhopper_key:
          // @ts-expect-error
          selectedDispatchJobs.value[0].originalJob.rockhopper_key, // TODO where is it?
      },
    })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        if (closeDetail === 'confirm') {
          emit('done', firstSelectedId.value);
        }
      },
    );
}
</script>

<template>
  <v-list
    :class="$style.dispatchJobMenuVList"
    data-testid="dispatchJobMenuVList"
  >
    <SecondarySideBarMenuVListItem
      v-if="isAllocatedTab"
      prepend-icon="mdi-account-remove-outline"
      :disabled="!isReadyToDeallocate"
      @click="handleDeallocateClick"
    >
      Unalloc
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      v-else
      prepend-icon="mdi-account-check-outline"
      :disabled="!isReadyToAllocate"
      @click="handleAllocateClick"
    >
      Allocate Job
      <v-hotkey inline :keys="hotkeys.ALLOCATE_SELECTED_JOBS" />
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-account-clock-outline"
      :disabled="!isReadyToPreAllocate"
      @click="handlePreAllocateClick"
    >
      Pre-Allocate Job
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      v-if="!isAllocatedTab"
      prepend-icon="mdi-account-remove-outline"
      :disabled="!isReadyToDeallocate"
      @click="handleDeallocateClick"
    >
      Deallocate Job
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-account-switch-outline"
      :disabled="!isReadyToAllocateAndSplit"
      @click="false"
    >
      Allocate & Split
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      v-if="isTargetLocked"
      prepend-icon="mdi-lock-open-outline"
      :disabled="!isReadyToUnlock"
      @click="handleUnlockClick"
    >
      Unlock Job
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      v-else
      prepend-icon="mdi-lock-outline"
      :disabled="!isReadyToLock"
      @click="handleLockClick"
    >
      Lock Job
    </SecondarySideBarMenuVListItem>
    <v-divider class="mt-2"></v-divider>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-clipboard-text-outline"
      :to="{
        name: routeNames.DETAILED_QUERY_JOB,
        query: _jobIdAndJobDateQuery,
      }"
      :disabled="!firstSelectedId"
    >
      Job Details
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-clipboard-clock-outline"
      :to="{
        name: routeNames.JOB_HISTORY,
        query: _jobIdAndJobDateQuery,
      }"
      :disabled="!firstSelectedId"
    >
      Job History
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-pencil-outline"
      :disabled="!selectedDispatchJobs.length"
      @click="handleEditJobClick"
    >
      Edit Job
    </SecondarySideBarMenuVListItem>
    <v-divider class="mt-2"></v-divider>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-truck-outline"
      :disabled="!isReadyToPickup"
      @click="false"
    >
      Pickup
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-call-split"
      :disabled="!isReadyToSplitToBase"
      @click="handleSplitToBaseClick"
    >
      Split to Base
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-check"
      :disabled="!isReadyToPoD"
      @click="false"
    >
      PoD
    </SecondarySideBarMenuVListItem>
    <v-divider class="mt-2"></v-divider>
    <SecondarySideBarMenuVListItem prepend-icon="mdi-update" disabled>
      Change Site Code
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-clock-time-eight-outline"
      :disabled="!isReadyToEditTimestamps"
      @click="handleEditTimestampsClick"
    >
      Edit Timestamps
    </SecondarySideBarMenuVListItem>
    <v-divider class="mt-2"></v-divider>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-undo"
      @click="handleUndoAllClick"
    >
      Undo All
    </SecondarySideBarMenuVListItem>
    <v-divider class="mt-2"></v-divider>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-content-duplicate"
      :disabled="!isReadyToClone"
      @click="handleCloneClick"
    >
      Clone
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-close"
      :disabled="!isReadyToCancel"
      @click="handleCancelClick"
    >
      Cancel
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-account-alert-outline"
      :disabled="!isReadyToFutile"
      @click="handleFutileClick"
    >
      Futile
    </SecondarySideBarMenuVListItem>
    <SecondarySideBarMenuVListItem
      prepend-icon="mdi-clipboard-alert-outline"
      :disabled="!isReadyToQuery"
      @click="handleQueryClick"
    >
      Query
    </SecondarySideBarMenuVListItem>
  </v-list>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.dispatchJobMenuVList {
  font-size: 1rem;
  line-height: 24px;
  padding: 8px 0px !important;
}
</style>
